/**
 * Metaverse Service
 * Manages 3D virtual world, avatars, and multiplayer interactions
 * Browser-based (no VR headset required, but VR-ready)
 */

class MetaverseService {
  constructor() {
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.players = new Map();
    this.currentPlayer = null;
    this.zones = [];
    this.isInitialized = false;
  }

  /**
   * Initialize the metaverse world
   */
  async initialize(containerId, userId, userName) {
    try {
      // This is a placeholder - actual Three.js implementation would go here
      // For now, we'll create the data structures and API
      
      this.currentPlayer = {
        id: userId,
        name: userName,
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        avatar: this.getDefaultAvatar(),
        zone: 'spawn',
        isMoving: false,
        isTalking: false
      };
      
      // Initialize zones
      this.zones = [
        {
          id: 'spawn',
          name: 'Welcome Plaza',
          description: 'Start your coding journey here!',
          position: { x: 0, y: 0, z: 0 },
          type: 'social',
          capacity: 100,
          players: []
        },
        {
          id: 'python_park',
          name: 'Python Park',
          description: 'Learn Python in this beautiful park',
          position: { x: 100, y: 0, z: 0 },
          type: 'learning',
          topic: 'python',
          capacity: 50,
          players: []
        },
        {
          id: 'javascript_jungle',
          name: 'JavaScript Jungle',
          description: 'Explore JavaScript in the wild',
          position: { x: 200, y: 0, z: 0 },
          type: 'learning',
          topic: 'javascript',
          capacity: 50,
          players: []
        },
        {
          id: 'code_arena',
          name: 'Code Battle Arena',
          description: 'Compete in coding challenges',
          position: { x: 0, y: 0, z: 100 },
          type: 'competition',
          capacity: 100,
          players: []
        },
        {
          id: 'art_gallery',
          name: 'Code Art Gallery',
          description: 'Display your code creations',
          position: { x: -100, y: 0, z: 0 },
          type: 'showcase',
          capacity: 50,
          players: []
        },
        {
          id: 'coding_cafe',
          name: 'Coding Café',
          description: 'Hangout and chat with friends',
          position: { x: 0, y: 0, z: -100 },
          type: 'social',
          capacity: 30,
          players: []
        },
        {
          id: 'tutorial_tower',
          name: 'Tutorial Tower',
          description: 'Guided lessons on every floor',
          position: { x: -200, y: 0, z: 0 },
          type: 'learning',
          capacity: 40,
          players: []
        },
        {
          id: 'project_lab',
          name: 'Project Lab',
          description: 'Build projects collaboratively',
          position: { x: 100, y: 0, z: 100 },
          type: 'collaborative',
          capacity: 20,
          players: []
        }
      ];
      
      this.isInitialized = true;
      console.log('🌍 Metaverse initialized!');
      
      return { success: true, player: this.currentPlayer };
    } catch (error) {
      console.error('Error initializing metaverse:', error);
      return { success: false, error };
    }
  }

  /**
   * Get default avatar options
   */
  getDefaultAvatar() {
    return {
      type: 'student',
      skin: '#ffdbac',
      hair: '#8b4513',
      outfit: '#4169e1',
      accessory: 'backpack',
      emoji: '😊',
      customization: {
        hat: null,
        glasses: null,
        pet: null
      }
    };
  }

  /**
   * Available avatar customizations
   */
  getAvatarOptions() {
    return {
      types: ['student', 'teacher', 'robot', 'ninja', 'wizard'],
      skins: ['#ffdbac', '#f1c27d', '#e0ac69', '#c68642', '#8d5524'],
      hairs: ['#000000', '#8b4513', '#ffff00', '#ff0000', '#0000ff'],
      outfits: ['#4169e1', '#ff1493', '#32cd32', '#ff8c00', '#9370db'],
      accessories: ['backpack', 'laptop', 'headphones', 'skateboard', 'book'],
      hats: ['cap', 'beanie', 'wizard_hat', 'crown', 'headband'],
      glasses: ['normal', 'sunglasses', 'vr_headset', 'monocle'],
      pets: ['cat', 'dog', 'dragon', 'robot', 'unicorn']
    };
  }

  /**
   * Update player avatar
   */
  updateAvatar(customization) {
    if (!this.currentPlayer) return { success: false };
    
    this.currentPlayer.avatar = {
      ...this.currentPlayer.avatar,
      ...customization
    };
    
    // Broadcast avatar update to other players
    this.broadcastUpdate('avatar_changed', {
      playerId: this.currentPlayer.id,
      avatar: this.currentPlayer.avatar
    });
    
    return { success: true, avatar: this.currentPlayer.avatar };
  }

  /**
   * Move player
   */
  movePlayer(direction, speed = 1) {
    if (!this.currentPlayer) return;
    
    const movement = { x: 0, y: 0, z: 0 };
    
    switch (direction) {
      case 'forward':
        movement.z = -speed;
        break;
      case 'backward':
        movement.z = speed;
        break;
      case 'left':
        movement.x = -speed;
        break;
      case 'right':
        movement.x = speed;
        break;
      case 'up':
        movement.y = speed;
        break;
      case 'down':
        movement.y = -speed;
        break;
      default:
        // Unknown direction
        break;
    }
    
    this.currentPlayer.position.x += movement.x;
    this.currentPlayer.position.y += movement.y;
    this.currentPlayer.position.z += movement.z;
    
    // Check if entered new zone
    this.checkZoneEntry();
    
    // Broadcast position to other players
    this.broadcastUpdate('player_moved', {
      playerId: this.currentPlayer.id,
      position: this.currentPlayer.position
    });
  }

  /**
   * Check if player entered a new zone
   */
  checkZoneEntry() {
    for (const zone of this.zones) {
      const distance = Math.sqrt(
        Math.pow(this.currentPlayer.position.x - zone.position.x, 2) +
        Math.pow(this.currentPlayer.position.z - zone.position.z, 2)
      );
      
      // Within 50 units = entered zone
      if (distance < 50 && this.currentPlayer.zone !== zone.id) {
        this.enterZone(zone.id);
        break;
      }
    }
  }

  /**
   * Enter a zone
   */
  enterZone(zoneId) {
    const zone = this.zones.find(z => z.id === zoneId);
    if (!zone) return;
    
    // Leave current zone
    if (this.currentPlayer.zone) {
      const oldZone = this.zones.find(z => z.id === this.currentPlayer.zone);
      if (oldZone) {
        oldZone.players = oldZone.players.filter(p => p !== this.currentPlayer.id);
      }
    }
    
    // Enter new zone
    this.currentPlayer.zone = zoneId;
    zone.players.push(this.currentPlayer.id);
    
    console.log(`🌍 Entered: ${zone.name}`);
    
    // Emit zone entry event
    const event = new CustomEvent('zone-entered', {
      detail: { zone, playersInZone: zone.players.length }
    });
    document.dispatchEvent(event);
    
    return zone;
  }

  /**
   * Get nearby players
   */
  getNearbyPlayers(radius = 50) {
    const nearby = [];
    
    for (const [id, player] of this.players.entries()) {
      if (id === this.currentPlayer.id) continue;
      
      const distance = Math.sqrt(
        Math.pow(this.currentPlayer.position.x - player.position.x, 2) +
        Math.pow(this.currentPlayer.position.y - player.position.y, 2) +
        Math.pow(this.currentPlayer.position.z - player.position.z, 2)
      );
      
      if (distance <= radius) {
        nearby.push({
          ...player,
          distance
        });
      }
    }
    
    return nearby.sort((a, b) => a.distance - b.distance);
  }

  /**
   * Send chat message
   */
  sendChatMessage(message, type = 'local') {
    if (!this.currentPlayer) return;
    
    const chatMessage = {
      id: Date.now(),
      playerId: this.currentPlayer.id,
      playerName: this.currentPlayer.name,
      message,
      type, // local, zone, global
      zone: this.currentPlayer.zone,
      timestamp: Date.now()
    };
    
    // Broadcast based on type
    if (type === 'local') {
      // Only nearby players (radius 30)
      this.broadcastToNearby('chat_message', chatMessage, 30);
    } else if (type === 'zone') {
      // Everyone in current zone
      this.broadcastToZone('chat_message', chatMessage, this.currentPlayer.zone);
    } else {
      // Global to everyone
      this.broadcastUpdate('chat_message', chatMessage);
    }
    
    return chatMessage;
  }

  /**
   * Perform emote/animation
   */
  performEmote(emote) {
    if (!this.currentPlayer) return;
    
    const emoteData = {
      playerId: this.currentPlayer.id,
      emote, // wave, dance, jump, celebrate, etc.
      timestamp: Date.now()
    };
    
    this.broadcastUpdate('player_emote', emoteData);
  }

  /**
   * Start collaborative coding session
   */
  startCollabSession(projectData) {
    const session = {
      id: `collab_${Date.now()}`,
      hostId: this.currentPlayer.id,
      hostName: this.currentPlayer.name,
      projectData,
      participants: [this.currentPlayer.id],
      maxParticipants: 4,
      zone: this.currentPlayer.zone,
      startTime: Date.now()
    };
    
    // Broadcast session invitation to zone
    this.broadcastToZone('collab_session_started', session, this.currentPlayer.zone);
    
    return session;
  }

  /**
   * Join collaborative session
   */
  joinCollabSession(sessionId) {
    // Send request to join
    this.broadcastUpdate('collab_join_request', {
      sessionId,
      playerId: this.currentPlayer.id,
      playerName: this.currentPlayer.name
    });
  }

  /**
   * Get all zones
   */
  getZones() {
    return this.zones.map(zone => ({
      ...zone,
      playersInZone: zone.players.length,
      isFull: zone.players.length >= zone.capacity
    }));
  }

  /**
   * Get current zone info
   */
  getCurrentZone() {
    return this.zones.find(z => z.id === this.currentPlayer?.zone);
  }

  /**
   * Teleport to zone
   */
  teleportToZone(zoneId) {
    const zone = this.zones.find(z => z.id === zoneId);
    if (!zone) return { success: false, error: 'Zone not found' };
    
    if (zone.players.length >= zone.capacity) {
      return { success: false, error: 'Zone is full' };
    }
    
    // Update position
    this.currentPlayer.position = { ...zone.position };
    
    // Enter zone
    this.enterZone(zoneId);
    
    return { success: true, zone };
  }

  /**
   * Broadcast update to all players
   */
  broadcastUpdate(event, data) {
    // In real implementation, this would use WebSocket/WebRTC
    // For now, just emit custom event
    const customEvent = new CustomEvent('metaverse-update', {
      detail: { event, data }
    });
    document.dispatchEvent(customEvent);
  }

  /**
   * Broadcast to nearby players
   */
  broadcastToNearby(event, data, radius) {
    const nearby = this.getNearbyPlayers(radius);
    // Send to nearby players only
    this.broadcastUpdate(`${event}_nearby`, { ...data, recipients: nearby.map(p => p.id) });
  }

  /**
   * Broadcast to zone
   */
  broadcastToZone(event, data, zoneId) {
    const zone = this.zones.find(z => z.id === zoneId);
    if (!zone) return;
    
    // Send to all players in zone
    this.broadcastUpdate(`${event}_zone`, { ...data, recipients: zone.players });
  }

  /**
   * Add another player (from server)
   */
  addPlayer(playerData) {
    this.players.set(playerData.id, playerData);
  }

  /**
   * Remove player (disconnected)
   */
  removePlayer(playerId) {
    this.players.delete(playerId);
    
    // Remove from all zones
    this.zones.forEach(zone => {
      zone.players = zone.players.filter(p => p !== playerId);
    });
  }

  /**
   * Get player count
   */
  getPlayerCount() {
    return this.players.size + 1; // +1 for current player
  }

  /**
   * Disconnect from metaverse
   */
  disconnect() {
    if (!this.currentPlayer) return;
    
    // Leave current zone
    const zone = this.getCurrentZone();
    if (zone) {
      zone.players = zone.players.filter(p => p !== this.currentPlayer.id);
    }
    
    // Notify others
    this.broadcastUpdate('player_left', {
      playerId: this.currentPlayer.id,
      playerName: this.currentPlayer.name
    });
    
    this.currentPlayer = null;
    this.isInitialized = false;
    
    console.log('🌍 Disconnected from metaverse');
  }
}

const metaverseService = new MetaverseService();
export default metaverseService;
